import { useCallback, useState } from 'react';
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router';
import toast from 'react-hot-toast';
import { GlassCard } from '@/components/GlassCard';
import { createCompany } from '@/features/companies/api';
import type { CompanyType } from '@/features/companies/api';
import { AdminCredentialsDialog } from '@/features/companies/AdminCredentialsDialog';
import type { AdminCredsView } from '@/features/companies/AdminCredentialsDialog';
import { CertificateDropzone } from '@/features/companies/CertificateDropzone';
import type { CertificateFields } from '@/features/companies/moic/fields';

export default function CreateCompanyPage() {
  const navigate = useNavigate();
  const [nameLa, setNameLa] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [taxCode, setTaxCode] = useState('');
  const [companyType, setCompanyType] = useState<CompanyType>('accounting');
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState<AdminCredsView | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const canSubmit = nameLa.trim().length > 0 && nameEn.trim().length > 0;

  // All three fields come from the same certificate, so all three are replaced
  // together — a form mixing scanned and typed values has no way to show which
  // is which. Nothing is submitted: the operator still reviews and presses the
  // button.
  const handleFill = useCallback((f: CertificateFields) => {
    setNameLa(f.nameLa);
    setNameEn(f.nameEn);
    setTaxCode(f.taxCode);
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    const body = {
      nameLa: nameLa.trim(),
      nameEn: nameEn.trim(),
      taxCode: taxCode.trim() || null,
      companyType,
    };
    try {
      const res = await toast.promise(
        createCompany(body),
        { loading: 'ກຳລັງສ້າງບໍລິສັດ...', success: 'ສ້າງບໍລິສັດສຳເລັດ', error: 'ສ້າງບໍລິສັດບໍ່ສຳເລັດ' },
      );
      setCreated({
        username: res.adminUsername,
        password: res.adminPassword,
        created: true,
        nameLa: body.nameLa,
        nameEn: body.nameEn,
        pathName: res.pathName,
      });
      setDialogOpen(true);
    } catch {
      // toast.promise already displayed the error toast
    } finally {
      setLoading(false);
    }
  }

  function handleDialogClose() {
    setCreated(null);
    setDialogOpen(false);
    void navigate('/companies');
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h3">ສ້າງບໍລິສັດ</Typography>
      <GlassCard>
        <form onSubmit={(e) => { void handleSubmit(e); }}>
          <Stack spacing={3}>
            <CertificateDropzone onFill={handleFill} />
            <TextField
              label="ຊື່ (ລາວ)"
              value={nameLa}
              onChange={(e) => setNameLa(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="ຊື່ (ອັງກິດ)"
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="TIN"
              value={taxCode}
              onChange={(e) => setTaxCode(e.target.value)}
              fullWidth
            />
            <FormControl>
              <FormLabel>ປະເພດ</FormLabel>
              <RadioGroup
                row
                value={companyType}
                onChange={(e) => setCompanyType(e.target.value as CompanyType)}
              >
                <FormControlLabel value="accounting" control={<Radio />} label="ບັນຊີ" />
                <FormControlLabel value="tax" control={<Radio />} label="ພາສີ" />
              </RadioGroup>
            </FormControl>
            <Button
              type="submit"
              variant="contained"
              loading={loading}
              disabled={!canSubmit}
            >
              ສ້າງບໍລິສັດ
            </Button>
          </Stack>
        </form>
      </GlassCard>
      <AdminCredentialsDialog
        creds={created}
        open={dialogOpen}
        onClose={handleDialogClose}
      />
    </Stack>
  );
}
